import React, { useEffect, useRef, useCallback, useState } from "react";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { Button, Stack, Box } from "@curves/ui-base";
import useShowPDF from "@util/hook/useShowPDF";
import { parseDate } from "@util/moment";
import { objectToQueryString, isFunction, isEqual } from "@util/utils";
import useDocumentApi from "@apis/useDocumentApi";
import useInBodyApi from "@apis/useInBodyApi";
import useSignalr from "@root/signalr/useSignalr";

const ProcessCircleBox = props => {
	const { className, refresh, onClose: onCloseProps, onOpenImpedanceDialog: onOpenImpedanceDialogProps, peopleID: peopleIDProps = 0, referenceType: referenceTypeProps, isInbodyOnly } = props;
	const { postCancelMeasureApi, getCheckEvaluationApi } = useInBodyApi();
	const { getMultipleInformationApi } = useDocumentApi();
	const { getGeneratedInbodyPDF } = useInBodyApi();
	const [path, setPath] = useState(null);
	const { enqueueSnackbar } = useSnackbar();
	const showPDF = useShowPDF();
	const measureDateRef = useRef(null);
	const barRef = useRef(null);
	const contRef = useRef(null);
	const measureIDRef = useRef(null);
	const signalr = useSignalr();

	const generatedInbodyPDFApi = async _fn => {
		const measureID = measureIDRef.current;
		await getGeneratedInbodyPDF(measureID);
		isFunction(_fn) && _fn();
	};

	const handleProcess = useCallback(
		text => {
			const barDom = barRef.current;
			const contDom = contRef.current;
			let processNum = parseInt(text, 10);
			const r = barDom.getAttribute("r");
			const c = Math.PI * (r * 2);
			if (processNum < 0) processNum = 0;
			if (processNum > 100) processNum = 100;
			if (processNum === 100) {
				isInbodyOnly ? enqueueSnackbar("已測量完畢", { variant: "success" }) : enqueueSnackbar("量身完畢，即將開啟套表畫面", { variant: "success" });
			}
			if (isInbodyOnly) {
				onCloseProps();
			} else {
				const pct = ((100 - processNum) / 100) * c;
				barDom.style["stroke-dashoffset"] = pct;
				contDom.setAttribute("data-pct", processNum);
			}
			// eslint-disable-next-line
		},
		// eslint-disable-next-line
		[]
	);

	const handleTranscript = text => {
		const date = parseDate(text, "YYYY-MM-DD") || parseDate();
		if (peopleIDProps && date) {
			measureDateRef.current = date;
		}
	};

	// 刷新使用
	const handleMeasureIDToRefresh = text => {
		measureIDRef.current = text;
	};

	const handleCloseWindowOnClick = () => {
		onCloseProps();
	};

	const showMultipleInformation = useCallback(
		async () => {
			if (["3", "4", "5"].includes(String(referenceTypeProps))) return; // 顧客 教練 不會有綜合評估表和成績單
			const measureDate = measureDateRef.current;
			if (!peopleIDProps || !measureDate) return;
			const resp = await getCheckEvaluationApi({ memberID: peopleIDProps, measureDate });
			if (resp) {
				await getMultipleInformationApi(objectToQueryString({ memberID: peopleIDProps, measureDate }), 0, peopleIDProps);
			}
		},
		// eslint-disable-next-line
		[]
	);

	const handleOpenPDF = useCallback(
		text => {
			if (!isInbodyOnly) {
				setPath(text);
				const pdfModel = {
					open: true,
					pdfUrl: text,
					onClose: () => {
						setTimeout(() => showMultipleInformation(), 10);
					},
				};
				if (![0, null].some(x => isEqual(measureIDRef.current, x))) {
					pdfModel.onRefresh = generatedInbodyPDFApi;
				}else{
                    enqueueSnackbar("量身未完成，請再量一次", { variant: "error" });
                }
				showPDF(pdfModel);
			}
			signalr.stop();
			refresh && refresh();
		},
		// eslint-disable-next-line
		[showMultipleInformation]
	);

	const handleUnusualImpedance = useCallback(
		text => {
			onOpenImpedanceDialogProps(text);
		},
		// eslint-disable-next-line
		[]
	);

	const doSignalrConnect = async () => {
		// 先掛上事件 handler 再 start，避免連線建立到掛上 handler 之間漏接訊息
		await signalr.on("RECEIVE_INBODY_PROCESS", handleProcess);
		await signalr.on("RECEIVE_INBODY_UNUSUAL_IMPEDANCE", handleUnusualImpedance);
		await signalr.on("RECEIVE_INBODY_PDF_PATH", handleOpenPDF);
		await signalr.on("RECEIVE_INBODY_MEASUREID", handleMeasureIDToRefresh);
		await signalr.on("RECEIVE_INBODY_MEASURE_DAY", handleTranscript);
		await signalr.start();
	};

	const handleOpenPDFOnClick = useCallback(
		() => {
			// 不需要轉 源頭已經format
			if (path) {
				showPDF({
					open: true,
					pdfUrl: path,
					onClose: () => {
						setTimeout(() => showMultipleInformation(), 10);
					},
				});
			}
		},
		// eslint-disable-next-line
		[showMultipleInformation, path]
	);

	useEffect(
		() => {
			doSignalrConnect();
			return () => {
				signalr.stop();
				postCancelMeasureApi();
			};
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Box className={clsx("process-circle", className)} key={"process-circle"}>
				<Box className="cont" ref={contRef} data-pct={0}>
					<Box component="svg" className="svg" width="200" height="200" viewport="0 0 100 100" version="1.1" xmlns="http://www.w3.org/2000/svg">
						<Box component="circle" r="90" cx="100" cy="100" fill="transparent" strokeDasharray="565.48" strokeDashoffset="0"></Box>
						<Box component="circle" ref={barRef} className="bar" r="90" cx="100" cy="100" fill="transparent" strokeDasharray="565.48" strokeDashoffset=""></Box>
					</Box>
				</Box>
			</Box>
			{!!path ? (
				<Stack justifyContent="end" direction="row" spacing={2}>
					<Button onClick={handleOpenPDFOnClick} variant="outlined">
						套表
					</Button>
					<Button onClick={onCloseProps} variant="contained">
						關閉
					</Button>
				</Stack>
			) : (
				<Stack justifyContent="center" direction="row" spacing={2}>
					<Button variant="contained" onClick={handleCloseWindowOnClick}>
						取消量身
					</Button>
				</Stack>
			)}
		</React.Fragment>
	);
};

export default ProcessCircleBox;
